import { createContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { DateRange } from "@mui/x-date-pickers-pro";
import dayjs, { Dayjs } from "dayjs";
import { MRT_TableInstance } from "material-react-table";
import { FedReference, FundingPackage, FundingPackagesData, FundingPackageStatus } from "./types";
import { InternalUser } from "../types/app";
import { Document } from "../reports/dashboard/policyIssues/types";
import { stripDataUriPrefix } from "../utils/stripDataUriPrefix";
import { toBase64 } from "../utils/helpers";

// ----------------------------------------------------------------------

const FundingAuthorizationContext = createContext<FundingPackagesData>({} as FundingPackagesData);

const REFRESH_INTERVAL_SECONDS = 60;
// Older funded packages aren't looked up in Wired and show no Fed reference line
const FED_REFERENCE_LOOKBACK_DAYS = 30;

function FundingAuthorizationProvider({ children }: { children: JSX.Element }) {
  const today = dayjs();
  const defaultStartDate = dayjs().subtract(3, "days");
  const defaultEndDate = dayjs();
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([defaultStartDate, defaultEndDate]);
  const [loading, setLoading] = useState(true);
  const [fundingPackages, setFundingPackages] = useState<FundingPackage[]>([]);
  const [assignedToMe, setAssignedToMe] = useState<boolean>(true);
  const tableRef = useRef<MRT_TableInstance<FundingPackage> | null>(null);
  const [isDefaultFilter, setIsDefaultFilter] = useState(true);
  const [assignedToUser, setAssignedToUser] = useState<InternalUser | null>(null);
  const queryParams = new URLSearchParams(location.search);
  const assignedToId = queryParams.get("assignedtoid");
  const isUnassignedOnly = Number(assignedToId) == -1;

  const initialPackageIdsRef = useRef<Set<number> | null>(null);
  const [newRows, setNewRows] = useState<Set<number>>(new Set());
  const [fedReferences, setFedReferences] = useState<Record<number, FedReference[]>>({});
  const latestRequestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const resetPaginationFunc = () => {
    if (tableRef.current) {
      tableRef.current.resetPageIndex();
    }
  };

  const refreshData = async (showLoader = true, signal?: AbortSignal) => {
    latestRequestIdRef.current += 1;
    const reqId = latestRequestIdRef.current;

    if (showLoader) {
      setLoading(true);
    }

    try {
      const { data } = await axios.get<FundingPackage[]>("/api/FundingPackages/packages/getAll", { signal });
      const incomingIds = new Set(data.map((p) => p.id));

      if (!initialPackageIdsRef.current) {
        initialPackageIdsRef.current = new Set(incomingIds);
      } else {
        const originalIdSet = initialPackageIdsRef.current;
        const newlySeen = [...incomingIds].filter((id) => !originalIdSet.has(id));
        if (newlySeen.length > 0) {
          setNewRows((prev) => new Set([...prev, ...newlySeen]));
        }
      }

      if (mountedRef.current && reqId === latestRequestIdRef.current) {
        setFundingPackages(data);
        refreshFedReferences(data, reqId, signal);
      }
    } catch (err: any) {
      if (!(axios.isAxiosError(err) && err.code === "ERR_CANCELED")) {
        console.error(err);
      }
    } finally {
      if (mountedRef.current && reqId === latestRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  // Not awaited by refreshData so a slow Wired lookup never holds up the package list
  const refreshFedReferences = async (packages: FundingPackage[], reqId: number, signal?: AbortSignal) => {
    const lookbackStart = dayjs().subtract(FED_REFERENCE_LOOKBACK_DAYS, "days");
    const fundedIds = packages
      .filter((p) => p.status === FundingPackageStatus.Funded && !!p.fundedDate && dayjs(p.fundedDate).isAfter(lookbackStart))
      .map((p) => p.id);
    if (fundedIds.length === 0) {
      setFedReferences({});
      return;
    }

    try {
      const { data } = await axios.post<Record<number, FedReference[]>>(
        "/api/FundingPackages/packages/GetFedReferences",
        fundedIds,
        { signal }
      );
      if (mountedRef.current && reqId === latestRequestIdRef.current) {
        setFedReferences(data);
      }
    } catch (err: any) {
      if (!(axios.isAxiosError(err) && err.code === "ERR_CANCELED")) {
        console.error(err);
      }
    }
  };

  const fetchFundingPackages = async () => {
    await refreshData(true);
  };
 
  const getAndSetAssignedToUser = async () => {
    if (!assignedToId || isUnassignedOnly) {
      return;
    }
    const { data } = await axios.get<InternalUser>(
      `/api/clientphonebook/internalusers/getInternalUserById?id=${assignedToId}`
    );
    setAssignedToUser(data);
  };
  const saveUploadToMomentum = async (document: Document, file: File) => {
    const { data } = await axios.post<number>("/api/outstandingChecks/UploadDocument", {
      ...document,
      mTANumber: document.mTANumber,
      base64File: stripDataUriPrefix(await toBase64(file)),
      fileName: (document?.fileName || file?.name),
      subcategoryId: document?.subcategoryId,
      description: document?.description,
      applicationInitiator: "TitleQ.FundingPackages",
    });
    return data;
  };
  useEffect(() => {
    getAndSetAssignedToUser();
  }, [assignedToId]);

  useEffect(() => {
    mountedRef.current = true;
    const controller = new AbortController();

    refreshData(true, controller.signal);
    const intervalId = setInterval(() => refreshData(false, controller.signal), REFRESH_INTERVAL_SECONDS * 1000);

    return () => {
      mountedRef.current = false;
      clearInterval(intervalId);
      controller.abort();
    };
  }, [dateRange]);

  return (
    <FundingAuthorizationContext.Provider
      value={{
        setDateRange,
        dateRange,
        loading,
        fundingPackages,
        setFundingPackages,
        assignedToMe,
        setAssignedToMe,
        fetchFundingPackages,
        tableRef,
        isDefaultFilter,
        setIsDefaultFilter,
        getAndSetAssignedToUser,
        assignedToUser,
        saveUploadToMomentum,
        newRows,
        fedReferences
      }}
    >
      {children}
    </FundingAuthorizationContext.Provider>
  );
}

export { FundingAuthorizationProvider, FundingAuthorizationContext };
